// ─────────────────────────────────────────────────────────────────────────────
// FILE: src/components/HRComponents/LeaveApprovals.jsx
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-hot-toast"; 
import { CheckSquare, X, Eye, FileText, Check, Ban, ExternalLink } from "lucide-react";
import SummaryApi from "../../common/SummaryApi";
import api from "../../common/apiClient";
import SelectDropdown from "../../customStyles/SelectDropdown";
import ModernTable from "../ui/ModernTable";
import PageHeader from "../ui/PageHeader";
import { formatDateTime } from "../../helpers/formatDateTime";

const PRIMARY_COLOR = "#df1f26";   // Crimson Red Brand Color
const SECONDARY_COLOR = "#031269"; // Dark Blue Brand Color

const LeaveApprovals = ({ user }) => {
  // --- State Management ---
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedPolicyRequest] = useState(null);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState("pending");
  const [approverNote, setApproverNote] = useState("");
  
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);

  // Pagination Configuration
  const [currentPage, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // Status Filter configurations
  const statusOptions = [
    { label: "Pending Approvals", id: "pending" },
    { label: "Approved Requests", id: "approved" },
    { label: "Rejected Requests", id: "rejected" },
  ];

  // ── FETCH REPORTEES' LEAVE REQUESTS (Paginated with Status Filters) ──────────
  const fetchReporteeLeaves = useCallback(async () => {
    setIsLoading(true);
    try {
      const baseUrl = SummaryApi.getReporteesLeaves?.url || "/api/hr/leaves/reportees";
      const method = SummaryApi.getReporteesLeaves?.method || "get";

      // Inject filter query params cleanly
      const url = `${baseUrl}?status=${selectedStatusFilter}&page=${currentPage}&limit=${itemsPerPage}`;
      const res = await api({ url, method });

      if (res.data.success) {
        setRequests(res.data.data || []);
        setTotalItems(res.data.total || res.data.data.length);
      } else {
        toast.error(res.data.message || "Failed to retrieve reportee requests.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error loading employee leave requests.");
    } finally {
      setIsLoading(false);
    }
  }, [selectedStatusFilter, currentPage, itemsPerPage]);

  useEffect(() => {
    fetchReporteeLeaves();
  }, [fetchReporteeLeaves]);

  // Reset pagination to page 1 when status filters change
  useEffect(() => {
    setPage(1);
  }, [selectedStatusFilter]);

  // --- Drawer Helper Actions ---
  const openViewDrawer = (request) => {
    setSelectedPolicyRequest(request);
    setApproverNote(request.approverNote || "");
    setIsViewOpen(true);
  };

  const closeViewDrawer = () => {
    setIsViewOpen(false);
    setSelectedPolicyRequest(null);
    setApproverNote("");
  };

  // ── PROCESS LEAVE REQUEST (Approve / Reject Action) ────────────────────────
  const handleProcessRequest = async (status) => {
    if (!selectedRequest) return;
    
    setIsSubmitting(true);
    try {
      let url, method;
      
      if (status === "approved") {
        url = SummaryApi.approveLeaveRequest?.url(selectedRequest._id) || `/api/hr/leaves/${selectedRequest._id}/approve`;
        method = SummaryApi.approveLeaveRequest?.method || "put";
      } else {
        url = SummaryApi.rejectLeaveRequest?.url(selectedRequest._id) || `/api/hr/leaves/${selectedRequest._id}/reject`;
        method = SummaryApi.rejectLeaveRequest?.method || "put";
      }

      const res = await api({
        url,
        method,
        data: { approverNote } // Pass managers notes / reason for rejection
      });

      if (res.data.success) {
        toast.success(res.data.message || `Leave request successfully ${status}!`);
        await fetchReporteeLeaves();
        closeViewDrawer();
      } else {
        toast.error(res.data.message || "Failed to process request.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error processing leave request.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Table Columns Mapping ---
  const columns = [
    {
      key: "employee",
      label: "Employee Details",
      render: (row) => (
        <div className="flex items-center gap-3">
          {/* Avatar Rendering */}
          <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center font-bold text-xs uppercase" style={{ color: SECONDARY_COLOR }}>
            {row.userId?.profilePicture ? (
              <img src={row.userId.profilePicture} alt="Avatar" className="h-full w-full rounded-full object-cover" />
            ) : (
              `${row.userId?.firstName?.charAt(0) || ""}${row.userId?.lastName?.charAt(0) || ""}`
            )}
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-gray-800">{`${row.userId?.firstName || ""} ${row.userId?.lastName || ""}`}</span>
            <span className="text-[10px] text-gray-400 font-mono font-bold">{row.userId?.userId || "No ID"}</span>
          </div>
        </div>
      ),
    },
    {
      key: "leaveType",
      label: "Leave Type",
      render: (row) => (
        <span
          className="px-2.5 py-1 rounded text-xs font-bold uppercase tracking-wider"
          style={{
            backgroundColor: `${row.leaveTypeId?.color || "#6366f1"}15`,
            color: row.leaveTypeId?.color || "#6366f1",
            border: `1px solid ${row.leaveTypeId?.color || "#6366f1"}30`,
          }}
        >
          {row.leaveTypeId?.code || "LV"}
        </span>
      ),
    },
    {
      key: "duration",
      label: "Duration",
      render: (row) => {
        const start = formatDateTime(row.startDate, false);
        const end = formatDateTime(row.endDate, false);
        return (
          <span className="font-semibold text-gray-700">
            {row.isHalfDay ? `${start} (Half-Day)` : `${start} - ${end}`}
          </span>
        );
      },
    },
    {
      key: "totalDays",
      label: "Total Days",
      render: (row) => <span className="font-bold text-gray-800">{row.totalDays} Day(s)</span>,
    },
    {
      key: "attachments",
      label: "Attachments",
      render: (row) => (
        <span className="text-xs font-semibold text-gray-500">
          {(row.attachments || []).length > 0 ? (
            <span className="text-blue-600 bg-blue-50 px-2 py-0.5 rounded border border-blue-100">
              {(row.attachments || []).length} Document(s)
            </span>
          ) : (
            "None"
          )}
        </span>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (row) => {
        const colors = {
          pending: "bg-yellow-50 text-yellow-700 border-yellow-200",
          approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
          rejected: "bg-red-50 text-red-700 border-red-200",
          cancelled: "bg-gray-100 text-gray-500 border-gray-200"
        };
        return (
          <span className={`px-2.5 py-0.5 rounded border text-xs font-bold uppercase tracking-wider ${colors[row.status]}`}>
            {row.status}
          </span>
        );
      },
    },
  ];

  const tableActions = [
    {
      label: selectedStatusFilter === "pending" ? "Review & Action" : "View Application Details",
      icon: <Eye className="w-4 h-4 text-blue-600" />,
      onClick: (row) => openViewDrawer(row),
    },
  ];

  return (
    <div className="p-4">
      <PageHeader
        title="Leave Approvals"
        description="Review incoming team leave applications, audit attachments, and authorize balance reductions."
        titleIcon={CheckSquare}
      />

      <div className="mt-6">
        {/* Toggle Filter Bar */}
        <div className="flex justify-end mb-4 max-w-xs ml-auto">
          <SelectDropdown
            label="Review Status"
            options={statusOptions}
            value={selectedStatusFilter}
            onChange={(val) => setSelectedStatusFilter(val)}
            labelKey="label"
            valueKey="id"
          />
        </div>

        {/* Paginated Requests list */}
        <div className="relative overflow-hidden">
          {isLoading && (
            <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] flex items-center justify-center z-10 transition-all rounded-2xl">
              <span className="text-xs font-bold text-gray-500 animate-pulse tracking-wide">Refreshing request queue...</span>
            </div>
          )}
          <ModernTable
            columns={columns}
            data={requests}
            actions={tableActions}
            selectable={false}
            actionType="dropdown"
            pagination={{
              totalItems,
              itemsPerPage,
              currentPage,
              onPageChange: (page) => setPage(page),
              onItemsPerPageChange: (limit) => {
                setItemsPerPage(limit);
                setPage(1);
              }
            }}
          />
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* RIGHT-SIDE UNIFIED LEAVE REVIEW DRAWER (Off-canvas) */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      <div
        className={`fixed inset-0 top-[57px] md:top-[65px] z-[65] overflow-hidden transition-all duration-300 ${
          isViewOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={closeViewDrawer} />
        
        <div className="absolute inset-y-0 right-0 max-w-full flex">
          <div
            className={`w-screen max-w-lg bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out ${
              isViewOpen ? "translate-x-0" : "translate-x-full"
            }`}
          >
            {/* Drawer Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-slate-50">
              <div>
                <h3 className="text-lg font-bold text-gray-900" style={{ color: SECONDARY_COLOR }}>
                  Review Leave Application
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">Examine dates, attachments, and submit approvals.</p>
              </div>
              <button
                type="button"
                onClick={closeViewDrawer}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Scrollable Review Body */}
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
              {/* Employee Summary Card */}
              <div className="bg-slate-50 p-4 rounded-2xl border border-gray-100 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center font-bold text-sm uppercase border border-gray-200">
                    {selectedRequest?.userId?.profilePicture ? (
                      <img src={selectedRequest.userId.profilePicture} alt="Avatar" className="h-full w-full rounded-full object-cover" />
                    ) : (
                      `${selectedRequest?.userId?.firstName?.charAt(0) || ""}${selectedRequest?.userId?.lastName?.charAt(0) || ""}`
                    )}
                  </div>
                  <div>
                    <div className="text-md font-bold text-gray-800">
                      {selectedRequest?.userId ? `${selectedRequest.userId.firstName} ${selectedRequest.userId.lastName}` : ""}
                    </div>
                    <div className="text-xs text-gray-400 font-semibold">{selectedRequest?.userId?.email}</div>
                  </div>
                </div>
              </div>

              {/* Leave request details */}
              <div className="space-y-4">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Application Parameters</h4>
                <div className="p-4 bg-white border border-gray-200 rounded-2xl shadow-sm space-y-3 text-sm">
                  <div className="flex justify-between items-center pb-2.5 border-b border-gray-50">
                    <span className="text-gray-500 font-medium">Leave Type:</span>
                    <span 
                      className="px-2.5 py-0.5 rounded text-xs font-bold uppercase tracking-wider"
                      style={{
                        backgroundColor: `${selectedRequest?.leaveTypeId?.color || "#6366f1"}15`,
                        color: selectedRequest?.leaveTypeId?.color || "#6366f1",
                        border: `1px solid ${selectedRequest?.leaveTypeId?.color || "#6366f1"}30`
                      }}
                    >
                      {selectedRequest?.leaveTypeId?.name} ({selectedRequest?.leaveTypeId?.code})
                    </span>
                  </div>

                  <div className="flex justify-between items-center pb-2.5 border-b border-gray-50">
                    <span className="text-gray-500 font-medium">Duration:</span>
                    <span className="font-bold text-gray-800">
                      {selectedRequest?.isHalfDay 
                        ? `${formatDateTime(selectedRequest.startDate, false)} (Half-Day)`
                        : `${formatDateTime(selectedRequest?.startDate, false)} - ${formatDateTime(selectedRequest?.endDate, false)}`
                      }
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 font-medium">Total Days Applied:</span>
                    <span className="font-bold text-gray-800">{selectedRequest?.totalDays} Day(s)</span>
                  </div>
                </div>
              </div>

              {/* Employee written reasoning */}
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Employee Reason</h4>
                <div className="p-3.5 bg-slate-50/50 rounded-xl border border-gray-100 text-sm text-gray-700 leading-relaxed font-medium">
                  "{selectedRequest?.reason || "No reasoning specified"}"
                </div>
              </div>

              {/* Attached documents list (Clickable links to open Cloudinary files in new tab) [2] */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Medical Certificates / Uploaded Proofs</h4>
                {(selectedRequest?.attachments || []).length === 0 ? (
                  <div className="text-xs text-gray-400 italic py-2">No files or certificate proofs attached to this application.</div>
                ) : (
                  <div className="space-y-2">
                    {(selectedRequest?.attachments || []).map((file, idx) => (
                      <a 
                        key={idx} 
                        href={file.fileUrl} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="flex items-center justify-between p-3 bg-blue-50/50 hover:bg-blue-50 border border-blue-100 rounded-xl text-blue-700 text-xs font-semibold transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          <span className="truncate max-w-[220px]">{file.fileName || "View Certificate Document"}</span>
                        </div>
                        <ExternalLink className="w-4 h-4 shrink-0" />
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Approver remarks textarea (Available only in pending mode) */}
              {selectedStatusFilter === "pending" && (
                <div className="space-y-2 border-t border-gray-100 pt-5">
                  <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase">Manager's Notes / Remarks (Optional)</label>
                  <textarea
                    rows={4}
                    value={approverNote}
                    onChange={(e) => setApproverNote(e.target.value)}
                    placeholder="Enter approval remarks or reason for rejection..."
                    className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-blue-100"
                  />
                </div>
              )}
            </div>

            {/* Drawer Footer Buttons (Approve / Reject triggers) */}
            <div className="px-6 py-4 border-t border-gray-100 bg-slate-50 flex items-center justify-end gap-3 shrink-0">
              <button
                type="button"
                onClick={closeViewDrawer}
                className="px-5 py-2.5 border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-100 transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>

              {selectedStatusFilter === "pending" ? (
                <>
                  {/* Reject Button Trigger */}
                  <button
                    type="button"
                    onClick={() => handleProcessRequest("rejected")}
                    disabled={isSubmitting}
                    className="flex items-center gap-1.5 px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-bold transition-colors"
                  >
                    <Ban className="w-4 h-4" />
                    <span>Reject</span>
                  </button>

                  {/* Approve Button Trigger */}
                  <button
                    type="button"
                    onClick={() => handleProcessRequest("approved")}
                    disabled={isSubmitting}
                    className="flex items-center gap-1.5 px-5 py-2.5 text-white rounded-lg text-sm font-bold transition-colors hover:opacity-90"
                    style={{ backgroundColor: SECONDARY_COLOR }}
                  >
                    <Check className="w-4 h-4" />
                    <span>Approve</span>
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={closeViewDrawer}
                  className="px-5 py-2.5 text-white text-sm font-bold rounded-lg transition-colors hover:bg-secondary"
                  style={{ backgroundColor: PRIMARY_COLOR }}
                >
                  Close Details
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaveApprovals;